import React, { useState } from "react";
import SEO from "../component/SEO";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDispatch } from 'react-redux';
import { setToken } from '../redux/userSlice';
import Cookies from 'js-cookie';
import { GoogleLogin } from '@react-oauth/google';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { FaFacebookF } from 'react-icons/fa';

function Signup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({});

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));

    // Clear error on typing
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!data.firstName.trim()) newErrors.firstName = "First name is required";
    if (!data.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!data.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!data.password) {
      newErrors.password = "Password is required";
    } else if (data.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!data.confirmPassword) {
      newErrors.confirmPassword = "Confirm your password";
    } else if (data.password !== data.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (data.phoneNumber.trim() && !/^\d{10,15}$/.test(data.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10-15 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    const { firstName, lastName, email, password, phoneNumber } = data;

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/signup/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email, password, phoneNumber }),
      });

      const result = await res.json();
      if (result.alert) {
        toast.success(result.message);
        navigate("/verify-email", { state: { email: data.email } });
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong, please try again");
    }
  };


  const inputClass = (field) =>
    `p-2 border rounded mb-1 ${errors[field] ? "border-red-500" : "border-gray-300"}`;

  return (
    <div className="p-6 flex justify-center items-center min-h-screen bg-gray-50">
      <SEO title="Sign Up - K Bakery" description="Create a new account at K Bakery." />
      <div className="bg-white shadow-lg p-6 rounded w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-6">Complete Signup</h1>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* First Name */}
          <label className="font-semibold mb-1">First Name</label>
          <input
            type="text"
            name="firstName"
            value={data.firstName}
            onChange={handleOnChange}
            className={inputClass("firstName")}
          />
          {errors.firstName && <span className="text-red-500 text-sm mb-2">{errors.firstName}</span>}

          {/* Last Name */}
          <label className="font-semibold mb-1">Last Name</label>
          <input
            type="text"
            name="lastName"
            value={data.lastName}
            onChange={handleOnChange}
            className={inputClass("lastName")}
          />
          {errors.lastName && <span className="text-red-500 text-sm mb-2">{errors.lastName}</span>}

          {/* Email */}
          <label className="font-semibold mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={data.email}
            onChange={handleOnChange}
            className={inputClass("email")}
          />
          {errors.email && <span className="text-red-500 text-sm mb-2">{errors.email}</span>}

          {/* Password */}
          <label className="font-semibold mb-1">Password</label>
          <input
            type="password"
            name="password"
            value={data.password}
            onChange={handleOnChange}
            className={inputClass("password")}
          />
          {errors.password && <span className="text-red-500 text-sm mb-2">{errors.password}</span>}

          {/* Confirm Password */}
          <label className="font-semibold mb-1">Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            value={data.confirmPassword}
            onChange={handleOnChange}
            className={inputClass("confirmPassword")}
          />
          {errors.confirmPassword && (
            <span className="text-red-500 text-sm mb-2">{errors.confirmPassword}</span>
          )}

          {/* Phone Number */}
          <label className="font-semibold mb-1">Phone Number</label>
          <input
            type="text"
            name="phoneNumber"
            value={data.phoneNumber}
            onChange={handleOnChange}
            className={inputClass("phoneNumber")}
          />
          {errors.phoneNumber && <span className="text-red-500 text-sm mb-2">{errors.phoneNumber}</span>}

          <button
            type="submit"
            className="mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 rounded"
          >
            Sign Up
          </button>
        </form>

          <div className="flex items-center my-4 before:flex-1 before:border-t before:border-gray-300 before:mt-0.5 after:flex-1 after:border-t after:border-gray-300 after:mt-0.5">
            <p className="text-center font-semibold mx-4 mb-0 text-sm">OR</p>
          </div>

          <div className="flex justify-center gap-4 mb-4">
            <GoogleLogin
               onSuccess={async (credentialResponse) => {
                 try {
                   const fetchData = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/google-login`, {
                     method: "POST",
                     headers: { "Content-Type": "application/json" },
                     body: JSON.stringify({ token: credentialResponse.credential })
                   });
                   const dataRes = await fetchData.json();
             
                   if (dataRes.alert && dataRes.token) {
                     toast.success('Successfully signed up with Google');
                     const userData = {
                       token: dataRes.token,
                       email: dataRes.data.email,
                       firstName: dataRes.data.firstName,
                       isAdmin: false,
                     };
                     dispatch(setToken(userData));
                     Cookies.set('token', dataRes.token, { expires: 30 });
                     localStorage.setItem("token", dataRes.token);
                     localStorage.setItem("email", dataRes.data.email);
                     localStorage.setItem("firstName", dataRes.data.firstName);
                     
                     setTimeout(() => navigate("/"), 1000);
                   } else {
                     toast.error(dataRes.message || "Google signup failed");
                   }
                 } catch (err) {
                   toast.error('Could not sign up with Google');
                 }
               }}
               onError={() => {
                 toast.error('Google Signup Failed');
               }}
            />

            <FacebookLogin
              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
              onSuccess={async (response) => {
                try {
                  const fetchData = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/facebook-login`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ accessToken: response.accessToken })
                  });
                  const dataRes = await fetchData.json();

                  if (dataRes.alert && dataRes.token) {
                    toast.success('Successfully signed up with Facebook');
                    const userData = {
                      token: dataRes.token,
                      email: dataRes.data.email,
                      firstName: dataRes.data.firstName,
                      isAdmin: false,
                    };
                    dispatch(setToken(userData));
                    Cookies.set('token', dataRes.token, { expires: 30 });
                    localStorage.setItem("token", dataRes.token);
                    localStorage.setItem("email", dataRes.data.email);
                    localStorage.setItem("firstName", dataRes.data.firstName);
                    
                    setTimeout(() => navigate("/"), 1000);
                  } else {
                    toast.error(dataRes.message || "Facebook signup failed");
                  }
                } catch (err) {
                  toast.error('Could not sign up with Facebook');
                }
              }}
              onFail={(error) => {
                console.error('Facebook Signup Failed', error);
                toast.error('Facebook Signup Failed');
              }}
              render={({ onClick }) => (
                <button type="button" onClick={onClick} className="bg-blue-600 hover:bg-blue-700 text-white rounded p-2 h-10 flex items-center justify-center min-w-[40px]">
                  <FaFacebookF />
                </button>
              )}
            />
          </div>

      </div>
    </div>
  );
}

export default Signup;
